// ── Admin drag / resize gesture ──
// One pointerdown on an admin-selected map object opens a gesture that can end three ways:
//   • long press (420ms)  → the radial menu; pointerup then runs the highlighted action
//   • movement past the   → drag (move mode) or proportional resize (resize mode),
//     threshold              persisted on pointerup
//   • neither             → a tap; the selection outline is (re)drawn and nothing is saved
// Which of move/resize applies is fixed at pointerdown from `scene.admin.objectModes`.
//
// Extracted verbatim from useMapAdminInteraction. The handler set below is ONE closure over
// twelve pieces of mutable gesture state (isDragging, longPressTriggered, the timer, the
// chrome, the captured origin…), so it deliberately stays whole: the pieces that could be
// lifted out without threading that state through a mutable bag — the outline chrome, the
// two backend writes, the radial-menu geometry — already have been (see the sibling modules).
import type { Dispatch, SetStateAction, MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import { MapObject } from '../../../models/mapObject';
import { impactOccurred } from '../../../utils/telegram/hapticFeedback';
import type { MapSceneState } from '../mapSceneState';
import {
  drawAdminSelectionOutline,
  drawAdminDragOutline,
  destroyAdminDragOutline,
  type AdminDragOutline,
} from './adminOutline';
import {
  openAdminRadialMenu,
  detectAdminRadialAction,
  markerClientPoint,
  type AdminRadialMenuDeps,
} from './adminRadialMenu';
import { saveAdminObjectResize, saveAdminObjectMove } from './adminObjectPersist';

const ADMIN_LONG_PRESS_MS = 420;
// Movement past this many pixels means the user is dragging, not holding — cancels the timer.
const ADMIN_LONG_PRESS_MOVE_THRESHOLD = 10;

export interface AdminDragAndDropDeps {
  sceneRef: MutableRefObject<MapSceneState>;
  radialMenu: AdminRadialMenuDeps;
  executeAdminRadialAction: (actionKey: string, mapObject: MapObject, marker: PIXI.Container) => void;
  clearAdminSelection: () => void;
  closeAdminRadialMenu: () => void;
  setAdminResizeDims: Dispatch<SetStateAction<{ w: number; h: number } | null>>;
  /** Drag-start distance in px, per pointer type (touch needs more slack than a mouse). */
  moveThreshold: { touch: number; mouse: number };
  queryClient: QueryClient;
}

export function setupAdminDragAndDrop(
  deps: AdminDragAndDropDeps,
  mapObject: MapObject,
  marker: PIXI.Container,
  event: PIXI.FederatedPointerEvent,
): void {
  const {
    sceneRef,
    radialMenu,
    executeAdminRadialAction,
    clearAdminSelection,
    closeAdminRadialMenu,
    setAdminResizeDims,
    moveThreshold,
    queryClient,
  } = deps;

  if (!sceneRef.current.pixi.viewport) return;

  // Stop event from bubbling to viewport handler (prevents clearAdminSelection race)
  event.stopPropagation();

  const initialScreenPoint = new PIXI.Point(event.global.x, event.global.y);
  const initialWorldPos = sceneRef.current.pixi.viewport.toWorld(initialScreenPoint);
  const initialMarkerX = marker.x;
  const initialMarkerY = marker.y;

  const dragOffsetX = initialWorldPos.x - initialMarkerX;
  const dragOffsetY = initialWorldPos.y - initialMarkerY;

  const isResizeMode = (sceneRef.current.admin.objectModes.get(mapObject.id) ?? 'dnd') === 'resize';

  let isDragging = false;
  let isViewportPaused = false;
  let longPressTriggered = false;
  let interactionFinished = false;
  let lastPointerClientX = event.client?.x ?? event.global.x;
  let lastPointerClientY = event.client?.y ?? event.global.y;
  let dragChrome: AdminDragOutline | null = null;
  const initialScaleX = marker.scale.x;
  const initialScaleY = marker.scale.y;

  // Clear previous admin selection outline (on any marker)
  clearAdminSelection();

  // Find the actual sprite (not Graphics overlays)
  const sprite = marker.children.find(c => c instanceof PIXI.Sprite) as PIXI.Sprite | undefined;
  const initialSpriteWidth = sprite ? Math.abs(sprite.width) : 0;
  const initialSpriteHeight = sprite ? Math.abs(sprite.height) : 0;

  const getMoveThreshold = (moveEvent: PIXI.FederatedPointerEvent): number => {
    const nativeEvent = moveEvent.nativeEvent as MouseEvent | PointerEvent;
    const pointerType = 'pointerType' in nativeEvent ? nativeEvent.pointerType : 'mouse';
    return (pointerType === 'touch' || pointerType === 'pen') ? moveThreshold.touch : moveThreshold.mouse;
  };

  // Long-press timer: opens radial menu after ADMIN_LONG_PRESS_MS
  let longPressTimer: number | null = window.setTimeout(() => {
    if (interactionFinished || !marker || marker.destroyed) return;
    longPressTriggered = true;
    // Unpause viewport so it doesn't stay locked
    if (sceneRef.current.pixi.viewport) sceneRef.current.pixi.viewport.pause = false;
    marker.cursor = 'pointer';
    // Get screen position for the menu
    const menuViewport = sceneRef.current.pixi.viewport;
    if (menuViewport) {
      const point = markerClientPoint(menuViewport, sceneRef.current.pixi.app, marker);
      openAdminRadialMenu(radialMenu, { mapObject, marker, screenX: point.x, screenY: point.y });
    }
    // Haptic feedback
    try {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
    } catch {
      // Haptic is optional
    }
  }, ADMIN_LONG_PRESS_MS);

  // Chrome is redrawn on every resize frame so the outline tracks the new sprite size.
  const drawGreenOutline = () => {
    const next = drawAdminDragOutline(marker, isResizeMode, dragChrome);
    if (next) dragChrome = next;
  };

  const finishInteraction = () => {
    if (interactionFinished) return;
    interactionFinished = true;
    window.removeEventListener('pointermove', onWindowPointerMove);
  };

  const cleanup = () => {
    if (longPressTimer !== null) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }
    finishInteraction();

    if (marker && !marker.destroyed) {
      marker.cursor = 'pointer';
      marker.scale.set(initialScaleX, initialScaleY);

      destroyAdminDragOutline(marker, dragChrome);
      dragChrome = null;

      marker.off('pointermove', handlePointerMove);
      marker.off('pointerup', handlePointerUp);
      marker.off('pointerupoutside', handlePointerUp);
      marker.off('pointercancel', handlePointerCancel);
    }

    if (sceneRef.current.pixi.viewport) {
      sceneRef.current.pixi.viewport.off('pointermove', handlePointerMove);
      if (isViewportPaused || isDragging) {
        sceneRef.current.pixi.viewport.pause = false;
      }
    }

    setAdminResizeDims(null);
  };

  // Window-level pointermove: track pointer position + highlight radial action when menu is open
  const onWindowPointerMove = (moveEvent: PointerEvent) => {
    lastPointerClientX = moveEvent.clientX;
    lastPointerClientY = moveEvent.clientY;
    if (!longPressTriggered) return;
    radialMenu.setHighlightedAdminAction(detectAdminRadialAction(sceneRef, moveEvent.clientX, moveEvent.clientY));
  };

  window.addEventListener('pointermove', onWindowPointerMove);

  const handlePointerMove = (moveEvent: PIXI.FederatedPointerEvent) => {
    if (!sceneRef.current.pixi.viewport) return;

    if (!marker || marker.destroyed) {
      cleanup();
      return;
    }

    // If long-press was triggered (radial menu open), don't drag
    if (longPressTriggered) return;

    const moveDistance = Math.sqrt(
      Math.pow(moveEvent.global.x - initialScreenPoint.x, 2) +
      Math.pow(moveEvent.global.y - initialScreenPoint.y, 2)
    );

    // Cancel long-press timer if pointer moved too far
    if (moveDistance > ADMIN_LONG_PRESS_MOVE_THRESHOLD && longPressTimer !== null) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }

    const currentScreenPoint = new PIXI.Point(moveEvent.global.x, moveEvent.global.y);
    const currentWorldPos = sceneRef.current.pixi.viewport.toWorld(currentScreenPoint);

    if (moveDistance > getMoveThreshold(moveEvent)) {
      if (!isDragging) {
        isDragging = true;
        impactOccurred('medium');
      }

      moveEvent.stopPropagation();

      if (isResizeMode && sprite && !sprite.destroyed && initialSpriteWidth > 0) {
        // Resize mode: scale proportionally based on horizontal delta
        const deltaX = currentWorldPos.x - initialWorldPos.x;
        const scaleFactor = Math.max(0.3, Math.min(3, 1 + deltaX / initialSpriteWidth));
        sprite.width = initialSpriteWidth * scaleFactor;
        sprite.height = initialSpriteHeight * scaleFactor;
        // Redraw outline to match new size
        drawGreenOutline();
        // Show live dimensions
        setAdminResizeDims({
          w: Math.round(Math.abs(sprite.width)),
          h: Math.round(Math.abs(sprite.height)),
        });
      } else if (!isResizeMode) {
        // Move mode
        const newWorldX = currentWorldPos.x - dragOffsetX;
        const newWorldY = currentWorldPos.y - dragOffsetY;

        if (!marker.destroyed) {
          marker.position.set(newWorldX, newWorldY);
          marker.zIndex = newWorldY;
        }
      }
    }
  };

  const handlePointerUp = async (upEvent: PIXI.FederatedPointerEvent) => {
    if (!sceneRef.current.pixi.viewport) return;

    if (!marker || marker.destroyed) {
      cleanup();
      return;
    }

    if (longPressTimer !== null) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }

    // If long-press triggered (radial menu is open), detect action under pointer and execute
    if (longPressTriggered) {
      const actionToRun = detectAdminRadialAction(sceneRef, lastPointerClientX, lastPointerClientY);
      if (actionToRun) {
        executeAdminRadialAction(actionToRun, mapObject, marker);
      }
      closeAdminRadialMenu();
      cleanup();
      // Keep persistent green outline after radial menu action
      if (marker && !marker.destroyed) {
        drawAdminSelectionOutline(sceneRef, marker, mapObject.id);
      }
      return;
    }

    const finalScreenPoint = new PIXI.Point(upEvent.global.x, upEvent.global.y);
    const moveDistance = Math.sqrt(
      Math.pow(upEvent.global.x - initialScreenPoint.x, 2) +
      Math.pow(upEvent.global.y - initialScreenPoint.y, 2)
    );
    const upMoveThreshold = getMoveThreshold(upEvent);

    const wasDragging = isDragging || moveDistance > upMoveThreshold;

    // Capture final sprite dimensions before cleanup
    const finalSpriteWidth = (sprite && !sprite.destroyed) ? Math.round(Math.abs(sprite.width)) : undefined;
    const finalSpriteHeight = (sprite && !sprite.destroyed) ? Math.round(Math.abs(sprite.height)) : undefined;

    cleanup();

    if (wasDragging && !marker.destroyed) {
      if (isDragging) {
        impactOccurred('medium');
      }

      if (isResizeMode && finalSpriteWidth && finalSpriteHeight) {
        await saveAdminObjectResize({
          mapObjectId: mapObject.id,
          marker,
          sprite,
          size: { width: finalSpriteWidth, height: finalSpriteHeight },
          rollbackSize: { width: initialSpriteWidth, height: initialSpriteHeight },
          queryClient,
        });
      } else {
        // Move mode: save new position
        const finalWorldPos = sceneRef.current.pixi.viewport.toWorld(finalScreenPoint);
        await saveAdminObjectMove({
          mapObjectId: mapObject.id,
          marker,
          position: { x: finalWorldPos.x - dragOffsetX, y: finalWorldPos.y - dragOffsetY },
          rollbackPosition: { x: initialMarkerX, y: initialMarkerY },
          queryClient,
        });
      }
    }

    // Keep persistent green outline after drag/tap
    if (marker && !marker.destroyed) {
      drawAdminSelectionOutline(sceneRef, marker, mapObject.id);
    }
  };

  const handlePointerCancel = () => {
    if (longPressTimer !== null) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }
    if (longPressTriggered) {
      closeAdminRadialMenu();
    }
    if (isDragging && marker && !marker.destroyed) {
      if (isResizeMode && sprite && !sprite.destroyed) {
        sprite.width = initialSpriteWidth;
        sprite.height = initialSpriteHeight;
      } else {
        marker.position.set(initialMarkerX, initialMarkerY);
        marker.zIndex = initialMarkerY;
      }
    }
    cleanup();
  };

  // Green outline in both modes; resize handle only in resize mode
  marker.cursor = isResizeMode ? 'nwse-resize' : 'grabbing';
  drawGreenOutline();

  if (sceneRef.current.pixi.viewport) {
    sceneRef.current.pixi.viewport.pause = true;
    isViewportPaused = true;
  }

  if (marker && !marker.destroyed) {
    marker.on('pointermove', handlePointerMove);
    marker.once('pointerup', handlePointerUp);
    marker.once('pointerupoutside', handlePointerUp);
    marker.once('pointercancel', handlePointerCancel);
  }

  if (sceneRef.current.pixi.viewport) {
    sceneRef.current.pixi.viewport.on('pointermove', handlePointerMove);
  }
}
